import { useEffect, useState, useCallback } from "react";
import { Check, Lock, Sparkles, Zap, Heart, Magnet, Coins, Hash, Crown } from "lucide-react";
import api from "../lib/api";

// Moonstone Store — sells Endless Mode run-starters, permanent perks, and
// cosmetic moonstone skins. Wallet balance is hard-capped at 100 server-side.
// Skins are bought once and equipped via /moonstones/skin/active. Starters
// stack into pending_starters and get auto-armed at the start of the next
// Endless run; perks are passive and apply forever once owned.
const ICON_FOR = {
  "pre-armed-shield": Crown,
  "extra-life-loadout": Heart,
  "wave-5-plasma": Zap,
  "stone-hunter": Hash,
  "lunar-magnet": Magnet,
  "frugal-pilot": Coins,
};

function CategorySection({ title, items, owned, pending, onBuy, locked, balance, accent }) {
  return (
    <section className="mb-8">
      <div className="flex items-center gap-2 mb-3">
        <Sparkles className="w-4 h-4" style={{ color: accent }} />
        <h3 className="font-heading font-black uppercase text-lg tracking-tight" style={{ color: accent }}>
          {title}
        </h3>
        <span className="font-mono-cosmic text-[10px] tracking-[0.3em] uppercase text-white/40 ml-auto">
          // {items.length} items
        </span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {items.map((item) => {
          const Icon = ICON_FOR[item.id] || Sparkles;
          const isOwned = owned.includes(item.id);
          const stacks = pending.filter((p) => p === item.id).length;
          const affordable = balance >= item.cost;
          // Perks/skins lock once owned. Starters stack so they're always buyable.
          const isPerk = item.category === "perk";
          const isSkin = item.category === "skin";
          const lockedOut = (isPerk || isSkin) && isOwned;
          return (
            <div
              key={item.id}
              className="glass p-4 border border-white/10 transition hover:border-white/30"
              style={{ borderColor: lockedOut ? `${accent}99` : undefined }}
              data-testid={`moonstone-store-card-${item.id}`}
            >
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="flex items-center gap-2 min-w-0">
                  <Icon className="w-4 h-4 flex-shrink-0" style={{ color: accent }} />
                  <h4
                    className="font-heading uppercase text-sm tracking-tight text-white truncate"
                    title={item.name}
                  >
                    {item.name}
                  </h4>
                </div>
                <span
                  className="font-mono-cosmic text-[10px] tracking-[0.25em] uppercase tabular-nums flex-shrink-0"
                  style={{ color: accent }}
                >
                  ☾ {item.cost}
                </span>
              </div>
              <p className="text-white/55 text-[11px] leading-snug mb-3 min-h-[2.4em]">
                {item.desc}
              </p>
              {/* Skin preview swatch — visualises the opal palette inline. */}
              {isSkin && item.palette && (
                <div className="flex items-center justify-center mb-3 h-10">
                  <div
                    className="w-9 h-9 rounded-full"
                    style={{
                      background: `radial-gradient(circle at 35% 35%, ${item.palette.core} 0%, ${item.palette.mid} 60%, ${item.palette.halo} 100%)`,
                      boxShadow: `0 0 12px ${item.palette.mid}aa`,
                    }}
                  />
                </div>
              )}
              {lockedOut ? (
                <div
                  className="flex items-center justify-center gap-1.5 px-3 py-2 border font-mono-cosmic text-[10px] tracking-[0.3em] uppercase"
                  style={{ borderColor: `${accent}aa`, color: accent }}
                  data-testid={`moonstone-store-owned-${item.id}`}
                >
                  <Check className="w-3 h-3" /> Owned
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => onBuy(item)}
                  disabled={!affordable || locked}
                  className={`w-full px-3 py-2 border font-mono-cosmic text-[10px] tracking-[0.3em] uppercase transition ${
                    affordable
                      ? "hover:bg-white/[0.06] cursor-pointer"
                      : "opacity-40 cursor-not-allowed"
                  }`}
                  style={{
                    borderColor: affordable ? `${accent}cc` : "rgba(255,255,255,0.15)",
                    color: affordable ? accent : "rgba(255,255,255,0.4)",
                  }}
                  data-testid={`moonstone-store-buy-${item.id}`}
                >
                  {affordable ? `Buy · ☾${item.cost}` : `Need ☾${item.cost}`}
                </button>
              )}
              {item.category === "starter" && stacks > 0 && (
                <div
                  className="mt-2 flex items-center justify-center gap-1.5 px-2 py-1 font-mono-cosmic text-[9px] tracking-[0.3em] uppercase"
                  style={{ borderColor: `${accent}66`, color: accent }}
                  data-testid={`moonstone-store-stacks-${item.id}`}
                >
                  · {stacks} pending
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

export default function MoonstoneStore() {
  const [store, setStore] = useState(null); // { cap, starters, perks, skins }
  const [wallet, setWallet] = useState(null); // { balance, cap, owned_*, active_skin }
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const refresh = useCallback(() => {
    setError(null);
    Promise.all([
      api.get("/moonstones/store").then((r) => r.data),
      api.get("/moonstones/me").then((r) => r.data),
    ])
      .then(([s, m]) => {
        setStore(s);
        setWallet(m);
      })
      .catch(() => setError("Failed to load Moonstone Store"));
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const onBuy = useCallback(async (item) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const r = await api.post("/moonstones/buy", { item_id: item.id });
      setWallet((w) => ({ ...(w || {}), ...(r.data?.wallet || {}) }));
      // Auto-equip a freshly-bought skin so the visual change is immediate.
      if (item.category === "skin") {
        const r2 = await api.post("/moonstones/skin/active", { skin_id: item.id });
        setWallet((w) => ({ ...(w || {}), ...(r2.data?.wallet || {}) }));
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || "Purchase failed";
      setError(detail);
    } finally {
      setBusy(false);
    }
  }, [busy]);

  const onEquipSkin = useCallback(async (id) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const r = await api.post("/moonstones/skin/active", { skin_id: id });
      setWallet((w) => ({ ...(w || {}), ...(r.data?.wallet || {}) }));
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to equip skin");
    } finally {
      setBusy(false);
    }
  }, [busy]);

  if (!store || !wallet) {
    return (
      <div className="font-mono-cosmic text-[10px] tracking-[0.35em] uppercase text-white/50 text-center py-12">
        // Loading Moonstone Store…
      </div>
    );
  }

  const balance = Number(wallet.balance || 0);
  const cap = Number(wallet.cap || 5000);
  const ownedPerks = wallet.owned_perks || [];
  const ownedSkins = wallet.owned_skins || [];
  const pending = wallet.pending_starters || [];
  const activeSkin = wallet.active_skin || "default";

  return (
    <div data-testid="moonstone-store">
      <div
        className="glass p-4 mb-6 border border-[#c084fc]/40 flex flex-wrap items-center justify-between gap-3"
        data-testid="moonstone-store-header"
      >
        <div className="flex items-center gap-3">
          <span
            className="text-2xl leading-none"
            style={{ color: "#c084fc", textShadow: "0 0 12px rgba(192,132,252,0.7)" }}
          >
            ☾
          </span>
          <div>
            <div className="font-mono-cosmic text-[9px] tracking-[0.3em] uppercase text-[#c084fc]">
              // Wallet
            </div>
            <div
              className="font-heading font-black text-2xl tracking-tighter text-white"
              data-testid="moonstone-wallet-balance"
            >
              {balance}
              <span className="text-white/40 text-base ml-1">/{cap}</span>
            </div>
          </div>
        </div>
        <div className="flex-1 max-w-xs">
          <div className="h-2 bg-white/10 overflow-hidden">
            <div
              className="h-full bg-[#c084fc] transition-all"
              style={{
                width: `${Math.min(100, (balance / cap) * 100)}%`,
                boxShadow: "0 0 8px rgba(192,132,252,0.6)",
              }}
            />
          </div>
          <div className="font-mono-cosmic text-[9px] tracking-[0.3em] uppercase text-white/40 mt-1">
            {balance >= cap ? "Wallet capped — spend before earning more" : "Earn stones in Endless Mode"}
          </div>
        </div>
      </div>

      {error && (
        <div
          className="mb-4 px-4 py-3 border border-[#ff007f]/60 bg-[#ff007f]/10 text-[#ff007f] font-mono-cosmic text-[10px] tracking-[0.3em] uppercase"
          data-testid="moonstone-store-error"
        >
          // {error}
        </div>
      )}

      <CategorySection
        title="Run Starters"
        items={store.starters}
        owned={[]}
        pending={pending}
        onBuy={onBuy}
        locked={busy}
        balance={balance}
        accent="#7afcff"
      />

      <CategorySection
        title="Permanent Perks"
        items={store.perks}
        owned={ownedPerks}
        pending={[]}
        onBuy={onBuy}
        locked={busy}
        balance={balance}
        accent="#ff66ff"
      />

      <section className="mb-2">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles className="w-4 h-4" style={{ color: "#c084fc" }} />
          <h3 className="font-heading font-black uppercase text-lg tracking-tight" style={{ color: "#c084fc" }}>
            Moonstone Skins
          </h3>
          <span className="font-mono-cosmic text-[10px] tracking-[0.3em] uppercase text-white/40 ml-auto">
            // {store.skins.length} skins
          </span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {store.skins.map((skin) => {
            const isOwned = ownedSkins.includes(skin.id);
            const isActive = skin.id === activeSkin;
            const affordable = balance >= skin.cost;
            return (
              <div
                key={skin.id}
                className="glass p-4 border transition hover:border-white/30 text-center"
                style={{
                  borderColor: isActive ? "#c084fc" : "rgba(255,255,255,0.1)",
                  boxShadow: isActive ? "0 0 16px rgba(192,132,252,0.5)" : undefined,
                }}
                data-testid={`moonstone-skin-card-${skin.id}`}
              >
                <div className="flex items-center justify-center mb-3 h-14">
                  <div
                    className="w-12 h-12 rounded-full"
                    style={{
                      background: `radial-gradient(circle at 35% 35%, ${skin.palette.core} 0%, ${skin.palette.mid} 60%, ${skin.palette.halo} 100%)`,
                      boxShadow: `0 0 14px ${skin.palette.mid}aa`,
                    }}
                  />
                </div>
                <h4 className="font-heading uppercase text-sm tracking-tight text-white mb-0.5">
                  {skin.name}
                </h4>
                <div className="font-mono-cosmic text-[9px] tracking-[0.25em] uppercase text-[#c084fc] mb-3 tabular-nums">
                  ☾ {skin.cost}
                </div>
                {isActive ? (
                  <div
                    className="flex items-center justify-center gap-1.5 px-3 py-2 border border-[#c084fc] text-[#c084fc] font-mono-cosmic text-[10px] tracking-[0.3em] uppercase"
                    data-testid={`moonstone-skin-active-${skin.id}`}
                  >
                    <Check className="w-3 h-3" /> Equipped
                  </div>
                ) : isOwned ? (
                  <button
                    type="button"
                    onClick={() => onEquipSkin(skin.id)}
                    disabled={busy}
                    className="w-full px-3 py-2 border border-white/30 hover:border-white text-white/85 hover:text-white font-mono-cosmic text-[10px] tracking-[0.3em] uppercase transition"
                    data-testid={`moonstone-skin-equip-${skin.id}`}
                  >
                    Equip
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => onBuy(skin)}
                    disabled={!affordable || busy}
                    className={`w-full px-3 py-2 border font-mono-cosmic text-[10px] tracking-[0.3em] uppercase transition ${
                      affordable
                        ? "border-[#c084fc] text-[#c084fc] hover:bg-[#c084fc]/10"
                        : "border-white/15 text-white/30 cursor-not-allowed"
                    }`}
                    data-testid={`moonstone-skin-buy-${skin.id}`}
                  >
                    {affordable ? `Buy · ☾${skin.cost}` : <span className="inline-flex items-center gap-1"><Lock className="w-3 h-3" />Locked</span>}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
}
